import { useState } from 'react';
import { Button, Card, Group, List, Stack, Title } from '@mantine/core';
import { IconArrowRight, IconCircle, IconCircleCheck } from '@tabler/icons-react';
import { Cmp } from '~portal/lib';
import { ChatMode, useChatModeStore } from '~portal/stores/chat-mode-store';
import * as styles from './ModeSelectionScreen.css';

type ModeOption = {
  mode: ChatMode;
  title: string;
  bullets: string[];
};

const options: ModeOption[] = [
  {
    mode: 'answer',
    title: 'Answer',
    bullets: [
      'Short, sharp answers',
      'Faster processing times',
      'Uses one primary source of reference',
      'Tells you what you needed to know',
      'Best for quick and simple questions',
    ],
  },
  {
    mode: 'analyse',
    title: 'Analyse',
    bullets: [
      'Detailed, thoughtful responses',
      'Deeper research process',
      'Cross-references multiple sources',
      'Clear understanding of nuance',
      'Best for more complex questions',
    ],
  },
];

/**
 * @desc First-run mode-selection screen (Charlie rev 2, H2-181). Shown as the
 * empty state of a brand-new conversation until the user confirms a mode;
 * afterwards (or on any later new chat) the normal empty state takes over.
 * Mode can still be changed later from the composer's mode menu.
 */
export const ModeSelectionScreen: Cmp = function () {
  const mode = useChatModeStore((state) => state.mode);
  const setMode = useChatModeStore((state) => state.setMode);
  const confirmSelection = useChatModeStore((state) => state.confirmSelection);
  // Nothing is pre-selected on first run, even though the store always holds a
  // mode (Answer by default): the cards show a tick only once the user has
  // chosen one. From then on they mirror the store, so the cards and the
  // composer's mode menu are one control and never disagree (H2-238).
  const [hasChosen, setHasChosen] = useState(false);

  const choose = (chosen: ChatMode) => {
    setMode(chosen);
    setHasChosen(true);
  };

  return (
    <Stack className={styles.wrapper} gap="lg" align="center" justify="center">
      <Group className={styles.cardsRow} gap="lg" justify="center" align="stretch">
        {options.map((option) => {
          const isSelected = hasChosen && option.mode === mode;

          return (
            <Card
              key={option.mode}
              withBorder
              radius="md"
              p="xl"
              className={isSelected ? styles.cardSelected : styles.card}
              onClick={() => choose(option.mode)}
              role="radio"
              aria-checked={isSelected}
              tabIndex={0}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  choose(option.mode);
                }
              }}
            >
              <Group justify="space-between" align="flex-start" wrap="nowrap" mb="md">
                <Title order={2} className={styles.cardTitle}>
                  {option.title}
                </Title>
                {isSelected ? (
                  <IconCircleCheck size={30} className={styles.cardIcon} />
                ) : (
                  <IconCircle size={30} className={styles.cardIcon} />
                )}
              </Group>

              <List spacing="xs" size="sm" listStyleType="none" className={styles.bulletText}>
                {option.bullets.map((bullet) => (
                  <List.Item key={bullet}>{bullet}</List.Item>
                ))}
              </List>
            </Card>
          );
        })}
      </Group>

      {hasChosen && (
        <Button
          size="md"
          radius="xl"
          color="blue"
          rightSection={<IconArrowRight size={18} />}
          onClick={() => confirmSelection(mode)}
        >
          Confirm Selection
        </Button>
      )}
    </Stack>
  );
};
